import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { listAuditLog } from "../api/console-api";
import { PageTop } from "../shell/PageTop";
import { AUDIT_KINDS } from "@wallyo/contracts/console";
import { t } from "../i18n";
import { useApi } from "../api/context";
import { useHash } from "../navigation/use-hash";
import { useStaff } from "../shell/staff-context";
import {
  DEFAULT_EFFECT,
  auditHash,
  filterProblems,
  filtersFromHash,
  namesSomeone,
  type AuditFilters,
} from "./audit-filters";
import { INITIAL_AUDIT_LOG_STATE, auditLogReducer } from "./audit-log-state";
import { AuditLogView } from "./AuditLogView";

const SENSITIVE_VALUES_FEATURE = "VIEW_SENSITIVE_AUDIT_VALUES_STAFF_FEATURE_ENABLEMENT";
const VIEW_ACCOUNTS_FEATURE = "VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT";

/**
 * Journal d'audit: reads GET /api/v1/console/audit-log for the filters in the
 * URL hash. Every read is itself audited by the server. Reads retry by
 * themselves (the connection banner says so); a failure is shown with its
 * request ID and a retry.
 */
export function AuditScreen() {
  const { client } = useApi();
  const { me } = useStaff();
  const hash = useHash();
  const filters = useMemo(() => filtersFromHash(hash), [hash]);
  const key = auditHash(filters);
  const blocked = Object.keys(filterProblems(filters)).length > 0;
  // Nothing is read until the link names somebody: the search is a person and
  // a period, and the server refuses one that names nobody.
  const searched = namesSomeone(filters);
  const [state, dispatch] = useReducer(auditLogReducer, INITIAL_AUDIT_LOG_STATE);
  // Answers for filters no longer on screen are dropped.
  const generation = useRef(0);
  const [reloads, reload] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    if (blocked) return;
    if (!searched) {
      // A cleared search leaves nobody's entries behind on the next one.
      dispatch({ type: "reload" });
      return;
    }
    const mine = ++generation.current;
    dispatch({ type: "reload" });
    // Changes unless the reader asked for reads: the log records every read of
    // itself, so on a page of fifty the console's own views crowd out what
    // anybody actually changed.
    listAuditLog(client, { effect: DEFAULT_EFFECT, ...filters }).then(
      (page) => {
        if (generation.current === mine) dispatch({ type: "loaded", page });
      },
      (error: unknown) => {
        if (generation.current === mine) dispatch({ type: "failed", error });
      },
    );
    // `key` stands for `filters`: the same filters written the same way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, key, blocked, searched, reloads]);

  const loadMore = useCallback(() => {
    if (!state.nextCursor || state.loadingMore) return;
    const mine = generation.current;
    dispatch({ type: "loadMore" });
    listAuditLog(client, { effect: DEFAULT_EFFECT, ...filters, cursor: state.nextCursor }).then(
      (page) => {
        if (generation.current === mine) dispatch({ type: "loadedMore", page });
      },
      (error: unknown) => {
        if (generation.current === mine) dispatch({ type: "moreFailed", error });
      },
    );
  }, [client, filters, state.nextCursor, state.loadingMore]);

  const onFilters = useCallback((next: AuditFilters) => {
    window.location.hash = auditHash(next);
  }, []);

  return (
    <>
      <PageTop title={t("screen.audit")} />
      {/* The nav's nouns, so the log is filtered with the words it is
          configured with. Everything else is Actions: an action is not a
          setting, it is what the settings attach to. */}
      <div className="surface-tabs" role="tablist">
        {AUDIT_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            role="tab"
            aria-selected={filters.kind === kind}
            className={filters.kind === kind ? "surface-tab on" : "surface-tab"}
            onClick={() => onFilters({ ...filters, kind: filters.kind === kind ? undefined : kind })}
          >
            {t(`audit.kind.${kind}` as const)}
          </button>
        ))}
      </div>
      <AuditLogView
        filters={filters}
        state={state}
        canSeeSensitive={(me.features as readonly string[]).includes(SENSITIVE_VALUES_FEATURE)}
        canViewAccounts={(me.features as readonly string[]).includes(VIEW_ACCOUNTS_FEATURE)}
        onFilters={onFilters}
        onRetry={reload}
        onLoadMore={loadMore}
      />
    </>
  );
}
